import { ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react'
import { FormEvent, useEffect, useRef, useState } from 'react'

interface AuthGateProps {
  requestCode: (email: string, turnstileToken?: string) => Promise<string | undefined>
  verifyCode: (email: string, code: string) => Promise<void>
  googleClientId?: string
  turnstileSiteKey?: string
  onGoogle: (credential: string) => Promise<void>
}

interface GoogleWindow extends Window {
  google?: { accounts: { id: {
    initialize: (input: { client_id: string; callback: (response: { credential: string }) => void }) => void
    renderButton: (element: HTMLElement, options: Record<string, unknown>) => void
  } } }
}

interface TurnstileWindow extends Window {
  turnstile?: {
    render: (element: HTMLElement, options: {
      sitekey: string
      callback: (token: string) => void
      'expired-callback'?: () => void
    }) => string
    reset?: (widgetId?: string) => void
  }
}

const MAILBOXES: Array<{ domains: string[]; label: string; url: string }> = [
  { domains: ['gmail.com', 'googlemail.com'], label: 'Open Gmail', url: 'https://mail.google.com' },
  { domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'], label: 'Open Outlook', url: 'https://outlook.live.com/mail' },
  { domains: ['yahoo.com'], label: 'Open Yahoo Mail', url: 'https://mail.yahoo.com' },
  { domains: ['icloud.com', 'me.com', 'mac.com'], label: 'Open iCloud Mail', url: 'https://www.icloud.com/mail' },
  { domains: ['proton.me', 'protonmail.com'], label: 'Open Proton Mail', url: 'https://mail.proton.me' },
  { domains: ['gmx.ch', 'gmx.net', 'gmx.de'], label: 'Open GMX', url: 'https://www.gmx.net' },
  { domains: ['web.de'], label: 'Open WEB.DE', url: 'https://web.de' },
  { domains: ['bluewin.ch'], label: 'Open Bluewin', url: 'https://webmail.bluewin.ch' },
]

function mailboxFor(email: string): { label: string; url: string } | null {
  const domain = email.split('@')[1]?.toLowerCase()
  if (!domain) return null
  return MAILBOXES.find((mailbox) => mailbox.domains.includes(domain)) ?? null
}

function GoogleButton({ clientId, onCredential }: { clientId: string; onCredential: (credential: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let cancelled = false
    const render = () => {
      const google = (window as GoogleWindow).google
      if (cancelled || !google || !ref.current) return
      google.accounts.id.initialize({ client_id: clientId, callback: ({ credential }) => onCredential(credential) })
      google.accounts.id.renderButton(ref.current, { type: 'standard', theme: 'outline', size: 'large', shape: 'rectangular', text: 'continue_with', width: 348 })
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]')
    if (existing) {
      if ((window as GoogleWindow).google) render()
      else existing.addEventListener('load', render, { once: true })
    } else {
      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.dataset.googleIdentity = 'true'
      script.addEventListener('load', render, { once: true })
      document.head.appendChild(script)
    }
    return () => { cancelled = true }
  }, [clientId, onCredential])
  return <div className="google-button" ref={ref} />
}

function TurnstileWidget({ siteKey, onToken }: { siteKey: string; onToken: (token: string) => void }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    let cancelled = false
    const render = () => {
      const turnstile = (window as TurnstileWindow).turnstile
      if (cancelled || !turnstile || !ref.current || ref.current.childElementCount) return
      turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: onToken,
        'expired-callback': () => onToken(''),
      })
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]')
    if (existing) {
      if ((window as TurnstileWindow).turnstile) render()
      else existing.addEventListener('load', render, { once: true })
    } else {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
      script.async = true
      script.dataset.turnstile = 'true'
      script.addEventListener('load', render, { once: true })
      document.head.appendChild(script)
    }
    return () => { cancelled = true }
  }, [onToken, siteKey])
  return <div className="turnstile-widget" ref={ref} />
}

export function AuthGate({ requestCode, verifyCode, googleClientId, turnstileSiteKey, onGoogle }: AuthGateProps) {
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const mailbox = step === 'code' ? mailboxFor(email) : null

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      if (step === 'email') {
        const devCode = await requestCode(email, turnstileSiteKey ? turnstileToken : undefined)
        if (devCode) setCode(devCode)
        setStep('code')
      } else {
        await verifyCode(email, code)
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Try again.')
      if (step === 'email' && turnstileSiteKey) {
        // tokens are single-use: get a fresh one for the next attempt
        setTurnstileToken('')
        ;(window as TurnstileWindow).turnstile?.reset?.()
      }
    } finally {
      setBusy(false)
    }
  }

  const submitGoogle = async (credential: string) => {
    setBusy(true)
    setError('')
    try { await onGoogle(credential) }
    catch (submitError) { setError(submitError instanceof Error ? submitError.message : 'Try again.') }
    finally { setBusy(false) }
  }

  return (
    <main className="auth-gate">
      <header className="auth-header"><span className="wordmark">FOCUS</span></header>
      <form className="auth-card" onSubmit={submit}>
        {step === 'code' && <button className="auth-back" type="button" onClick={() => { setStep('email'); setError('') }}><ArrowLeft size={16} /></button>}
        <h1>{step === 'email' ? 'Sign in' : 'Check your email'}</h1>
        {step === 'email' ? (
          <input autoFocus autoComplete="email" inputMode="email" type="email" placeholder="Email" value={email} onChange={(event) => setEmail(event.target.value)} required />
        ) : (
          <input autoFocus autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="000000" value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} required />
        )}
        {step === 'email' && turnstileSiteKey && <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} />}
        {error && <p className="auth-error">{error}</p>}
        <button className="auth-submit" disabled={busy || (step === 'email' ? !email || (Boolean(turnstileSiteKey) && !turnstileToken) : code.length !== 6)}>
          {busy ? '…' : step === 'email' ? 'Continue' : 'Verify'}{!busy && <ArrowRight size={18} />}
        </button>
        {mailbox && (
          <a className="mailbox-link" href={mailbox.url} rel="noreferrer" target="_blank">
            {mailbox.label} <ExternalLink size={14} />
          </a>
        )}
        {step === 'email' && googleClientId && <><div className="auth-divider"><span>or</span></div><GoogleButton clientId={googleClientId} onCredential={(credential) => void submitGoogle(credential)} /></>}
      </form>
      <p className="auth-legal">
        <a href="/privacy.html">Privacy</a> · <a href="/terms.html">Terms</a>
      </p>
    </main>
  )
}
